/**
 * First-touch acquisition attribution, held in the browser (spec section 16).
 *
 * Most provider traffic arrives on joinherdbook.com, which 301s to `/providers`
 * with the query string intact. Whatever that first URL carried is the primary
 * measurement for the whole outreach campaign, and it has to survive the
 * visitor reading the homepage, wandering to `/owners`, and coming back before
 * they fill anything in (CLAUDE.md, spec section 05).
 *
 * So the record is captured once per browsing session and then left alone:
 *
 * - captured on the first app load of the session, from `location.search`,
 *   `document.referrer`, and the landing path;
 * - stored in `sessionStorage` under one key, with a generated session id;
 * - **never overwritten** by a later navigation. First touch wins, and
 *   `landingPath` stays the path the session actually started on.
 *
 * The server records one `attribution_touches` row per submission, so a second
 * visit through a different campaign is still captured — as a new session,
 * with its own first touch.
 *
 * Nothing here may throw. Attribution is always secondary to the signup: a
 * blocked `sessionStorage` (Safari private browsing, a locked-down embedded
 * browser) degrades to an in-memory record for the life of the page rather
 * than taking the form down with it.
 */

import { attributionSchema, type Attribution } from '../../shared/schemas/attribution';

/** Versioned so a shape change cannot be misread as a valid old record. */
export const ATTRIBUTION_STORAGE_KEY = 'mhb.attribution.v1';

/**
 * The landing page A/B assignment. `localStorage`, not `sessionStorage`: the
 * variant has to be the same page the visitor saw last week, or the test
 * measures the layout churn rather than the layouts.
 */
export const VARIANT_STORAGE_KEY = 'mhb.variant.v1';

/**
 * The two landing variants under test. Reading which one a visitor has is
 * `currentVariant()` in `lib/variant.ts`; this module only assigns it.
 */
export type Variant = 'a' | 'b';

function isVariant(value: unknown): value is Variant {
  return value === 'a' || value === 'b';
}

/**
 * Query parameters read off the landing URL. `gclid` and `fbclid` are mapped
 * onto the generic `click_id` so a future ad platform does not need a schema
 * change to be measurable.
 */
const CLICK_ID_PARAMS = ['click_id', 'gclid', 'fbclid', 'msclkid'] as const;

/** Used when `sessionStorage` is unavailable. Lives for one page load. */
let memoryRecord: Attribution | undefined;

function readStoredValue(): string | null {
  try {
    return window.sessionStorage.getItem(ATTRIBUTION_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStoredValue(value: string): void {
  try {
    window.sessionStorage.setItem(ATTRIBUTION_STORAGE_KEY, value);
  } catch {
    // Storage is unavailable or full. The in-memory copy still serves the
    // form on this page, which is the case that matters.
  }
}

function readStoredVariant(): Variant | null {
  try {
    const stored = window.localStorage.getItem(VARIANT_STORAGE_KEY);
    return isVariant(stored) ? stored : null;
  } catch {
    return null;
  }
}

function writeStoredVariant(variant: Variant): void {
  try {
    window.localStorage.setItem(VARIANT_STORAGE_KEY, variant);
  } catch {
    // Storage is blocked or full. The visitor still gets a variant for this
    // page load; they may simply get the other one next time.
  }
}

/**
 * An even split that does not lean on `Math.random`'s seeding when a real
 * source of randomness is available. Falls back when `crypto` is missing,
 * because an unevenly split test is still better than no page.
 */
function coinFlip(): Variant {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
      const [byte] = crypto.getRandomValues(new Uint8Array(1));
      return byte !== undefined && byte % 2 === 0 ? 'a' : 'b';
    }
  } catch {
    // fall through
  }
  return Math.random() < 0.5 ? 'a' : 'b';
}

/**
 * Resolves the visitor's landing variant, in precedence order:
 *
 * 1. an explicit `?v=a` or `?v=b` on the URL, which also becomes the sticky
 *    assignment so a shared or bookmarked link stays consistent;
 * 2. the assignment already in `localStorage`;
 * 3. a fresh 50/50 flip, persisted.
 *
 * Anything else in `?v=` is ignored rather than honoured, so a crawler
 * following `?v=control` does not create a third bucket in the reporting.
 */
function resolveVariant(params: URLSearchParams): Variant {
  const requested = params.get('v')?.trim().toLowerCase();
  if (isVariant(requested)) {
    writeStoredVariant(requested);
    return requested;
  }

  const stored = readStoredVariant();
  if (stored) return stored;

  const assigned = coinFlip();
  writeStoredVariant(assigned);
  return assigned;
}

/**
 * An opaque per-session id. `crypto.randomUUID` needs a secure context, so
 * there is a fallback — the id only has to be unique enough to group touches
 * from one session, and the shared schema drops anything malformed rather than
 * rejecting the submission.
 */
function newSessionId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    // fall through
  }
  return `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

function param(params: URLSearchParams, name: string): string | undefined {
  const value = params.get(name);
  return value === null || value.trim() === '' ? undefined : value;
}

function firstParam(params: URLSearchParams, names: readonly string[]): string | undefined {
  for (const name of names) {
    const value = param(params, name);
    if (value !== undefined) return value;
  }
  return undefined;
}

/**
 * Builds the record for the current page. The shared schema does the trimming,
 * the length caps, and the dropping of anything unusable — it is written so
 * that it cannot fail (see `attribution.ts` in shared/schemas).
 *
 * The landing variant is resolved here so it rides along with every flow the
 * attribution record already feeds: the signup snapshot on the lead, the
 * attribution touch, and every analytics event.
 */
function capture(): Attribution {
  const params = new URLSearchParams(window.location.search);
  return attributionSchema.parse({
    sessionId: newSessionId(),
    landingPath: window.location.pathname,
    referrer: document.referrer,
    utmSource: param(params, 'utm_source'),
    utmMedium: param(params, 'utm_medium'),
    utmCampaign: param(params, 'utm_campaign'),
    utmContent: param(params, 'utm_content'),
    utmTerm: param(params, 'utm_term'),
    ref: param(params, 'ref'),
    clickId: firstParam(params, CLICK_ID_PARAMS),
    variant: resolveVariant(params),
  });
}

/** Parses a stored record back into the schema shape, or null if unusable. */
function parseStored(raw: string | null): Attribution | null {
  if (raw === null) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    return attributionSchema.parse(parsed);
  } catch {
    return null;
  }
}

/**
 * The one part of a captured record that a later page may change.
 *
 * First touch is otherwise never rewritten, and the campaign fields here still
 * are not: `?v=` says which layout to show, not where the visitor came from.
 * An explicit override has to win mid-session as well as on the landing page,
 * or `myherdbook.com/providers?v=b` would show variant A to anyone who had
 * already opened the site today — which is exactly how the link gets used, by
 * the owner checking a layout and by a campaign pinned to one design.
 *
 * Returns the record unchanged when there is nothing to override, so the
 * common path allocates nothing.
 */
function applyVariantOverride(record: Attribution): Attribution {
  let requested: string | undefined;
  try {
    requested = new URLSearchParams(window.location.search).get('v')?.trim().toLowerCase();
  } catch {
    return record;
  }

  if (!isVariant(requested) || requested === record.variant) return record;

  writeStoredVariant(requested);
  const updated = { ...record, variant: requested };
  writeStoredValue(JSON.stringify(updated));
  return updated;
}

/**
 * Captures the session's attribution if it has not been captured yet, and
 * returns it either way. Safe to call on every render; the second call is a
 * `sessionStorage` read.
 *
 * Called from the app bootstrap so the record reflects the URL the session
 * *landed* on rather than whichever page the visitor happened to submit from.
 */
export function initAttribution(): Attribution {
  const existing = memoryRecord ?? parseStored(readStoredValue());
  if (existing) {
    memoryRecord = applyVariantOverride(existing);
    return memoryRecord;
  }

  const captured = capture();
  memoryRecord = captured;
  writeStoredValue(JSON.stringify(captured));
  return captured;
}

/**
 * The attribution to send with a submission. Initialises on first use so a
 * form is never left without one, even if bootstrap ordering changes.
 */
export function getAttribution(): Attribution {
  return initAttribution();
}

/** Test seam. Clears the session record so a test can capture a fresh one. */
export function resetAttributionForTests(): void {
  memoryRecord = undefined;
  try {
    window.sessionStorage.removeItem(ATTRIBUTION_STORAGE_KEY);
  } catch {
    // nothing to clear
  }
}
